import { FC, useEffect, useRef, useState } from 'react'

import { NOW_YEAR } from '../../../configuration'
import { useOnClickOutside } from '../../../hooks'
import * as S from './MonthMultiPicker.styles'
import { MonthMultiPickerProps, Period } from './MonthMultiPicker.types'
import { formatPeriod } from './MonthMultiPicker.utils'
import { MonthRangeGrid } from './MonthRangeGrid'
import { MonthPick, monthPickOf, pickMonth } from './monthRangeSelection'

export const MonthMultiPicker: FC<MonthMultiPickerProps> = ({
  className,
  label = 'Any Month',
  year = NOW_YEAR,
  onChange,
  currentSelection,
  onReset,
  showYear,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [period, setPeriod] = useState<Period>(currentSelection || {})

  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setPeriod(currentSelection || {})
  }, [currentSelection])

  const handleReset = () => {
    setPeriod({})
    onReset()
    setShowDatePicker(false)
  }

  const handleMonthClick = (pick: MonthPick) => {
    const nextPeriod = pickMonth({ period, pick })
    setPeriod(nextPeriod)
    if (!nextPeriod.end) return

    onChange({ start: nextPeriod.start, end: nextPeriod.end })
    setShowDatePicker(false)
  }

  useOnClickOutside(pickerRef, () => {
    if (period.start && !period.end) {
      handleMonthClick(monthPickOf(period.start))
      return
    }

    setShowDatePicker(false)
  })

  return (
    <>
      <S.Selector
        className={className}
        isSelected={showDatePicker}
        onClick={() => setShowDatePicker(prevState => !prevState)}
      >
        <S.Label>{label}</S.Label>
        <S.SelectionContainer>
          <S.Selection>
            {currentSelection ? formatPeriod(period, showYear) : 'Any month'}
          </S.Selection>
          <S.IconCalendarStyled />
        </S.SelectionContainer>
      </S.Selector>
      {showDatePicker && (
        <S.Picker ref={pickerRef}>
          <MonthRangeGrid period={period} fallbackYear={year} onMonthClick={handleMonthClick} />
          <S.Divider />
          <S.ClearButton onClick={handleReset}>Clear</S.ClearButton>
        </S.Picker>
      )}
    </>
  )
}
